import { useState, useCallback, useEffect } from 'react';
import { inicializarProposta, simularPropostaFlex } from '../../../services/api';
import { parseCurrency,formatCurrency } from '../../../utils/formatters';
import { PLANO_TO_API_KEY, FAIXA_TO_API, TIPO_PLANO_FLEX_TO_VALUE, TIPO_CORRECAO_TO_VALUE, ALL_PLANOS } from '../../../utils/constantes';

/**
 * Hook para gerenciar ações de simulação (inicialização e envio)
 * @param {Object} params - Parâmetros necessários
 * @returns {Object} Estado e ações de simulação
 */
export const useSimulationActions = ({
  formData,
  setFormData,
  propertySelection,
  planosDisponiveis,
  showSuccess,
  showError,
  intermediates
}) => {
  const [dadosInicializacao, setDadosInicializacao] = useState(null);
  const [valorFinal, setValorFinal] = useState(0);
  const [valorTabelaState, setValorTabelaState] = useState(0);
  const [loadingInicializacao, setLoadingInicializacao] = useState(false);
  const [loadingSimulacao, setLoadingSimulacao] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isPrintEnabled, setIsPrintEnabled] = useState(false);

  // Função de validação extraída
  const validateForm = useCallback(() => {
    const errors = [];
    
    if (!propertySelection.selectedUnidade) {
      errors.push('Selecione uma unidade');
      return errors;
    }
    
    if (!formData.plano) {
      errors.push('Selecione um plano de pagamento');
      return errors;
    }


    if (!formData.tipoFluxo) {
      errors.push('Tipo de Fluxo é obrigatório');
       return errors;
    }
    
    if (formData.cartaDeCredito) {
      if (!formData.valorCartaDeCredito || parseCurrency(formData.valorCartaDeCredito) === 0) {
        errors.push('Valor da Carta de Crédito é obrigatório');
        return errors;
      }
    } else if (!formData.ato || parseCurrency(formData.ato) === 0) {
      errors.push('Ato (1ª Parcela) é obrigatório');
      return errors;
    }

    // Obter o plano selecionado para validações específicas
    const planoSelecionado = planosDisponiveis[formData.plano];

    // Extrair valores uma única vez para ambas as validações. Com Carta de Crédito marcada, o
    // valor que efetivamente conta como ato é o da Carta (campo imutável, dedicado) — o campo Ato
    // fica desabilitado nesse caso e não deve ser usado na validação.
    const atoValue = formData.cartaDeCredito
      ? (parseCurrency(formData.valorCartaDeCredito) || 0)
      : (parseCurrency(formData.ato) || 0);
    const e2Value = parseCurrency(formData.e2) || 0;
    const e3Value = parseCurrency(formData.e3) || 0;
    const fgtsValue = parseCurrency(formData.fgts) || 0;
    const desconto = parseCurrency(formData.desconto) || 0;
    const somaAtos = atoValue + e2Value + e3Value;

    if(fgtsValue + desconto > valorFinal){  
      errors.push(`A soma do FGTS (R$ ${formatCurrency(fgtsValue)}) e Desconto (R$ ${formatCurrency(desconto)}) não pode ser maior que o valor final (R$ ${formatCurrency(valorFinal)})`);
      return errors;
    }

    // Validação específica para À Vista
    if (planoSelecionado === 'À Vista' && valorFinal > 0) {
      const valorFinalEfetivo = valorFinal - fgtsValue- desconto;
      const diferenca = Math.abs(somaAtos - valorFinalEfetivo);

      // Com Carta de Crédito marcada, o ato complementar — calculado e inserido pelo back-end
      // (CartaDeCreditoPropostaService) com base no ato realmente calculado, não no valorFinal
      // local — cobre a diferença entre o valor da carta e o que falta. Por isso essa soma não
      // precisa bater com o valor final aqui no front quando a carta está ativa; validar isso
      // localmente bloquearia justamente o cenário para o qual o complementar existe.
      if (!formData.cartaDeCredito) {
        // Tolerância de 0.01 para evitar problemas de arredondamento
        if (diferenca >= 0.02) {
          errors.push(`A soma do ATO + Entradas (${formatCurrency(somaAtos)}) deve ser igual ao valor final (R$ ${formatCurrency(valorFinalEfetivo)})`);
          return errors;
        }
      }
      
      // Validação: E2 não pode ser 0 ou 0.01 (valor mínimo forçado) quando E3 tiver valor
      console.log('Validando E2 e E3:', { e2Value, e3Value });
      if ((e2Value === 0 || e2Value === 0.01) && e3Value > 0) {
        errors.push('E2 não pode ser 0 quando E3 tiver valor');
      }
       return errors;
    }

    // Validação para todos os planos: A soma dos atos com FGTS não pode ultrapassar o valor final
    if (valorFinal > 0) {
      const somaAtosComFgts = somaAtos + fgtsValue;
      
      console.log('Validando soma dos atos com FGTS:', { somaAtos, fgtsValue, somaAtosComFgts, valorFinal });
      // Tolerância de 0.01 para evitar problemas de arredondamento
      if (somaAtosComFgts > valorFinal + 0.01) {
        if (fgtsValue > 0) {
          errors.push(`A soma dos atos (R$ ${formatCurrency(somaAtos)}) + FGTS (R$ ${formatCurrency(fgtsValue)}) = R$ ${formatCurrency(somaAtosComFgts)} não pode ultrapassar o valor final (R$ ${formatCurrency(valorFinal)})`);
        } else {
          errors.push(`A soma dos atos (R$ ${formatCurrency(somaAtos)}) não pode ultrapassar o valor final (R$ ${formatCurrency(valorFinal)})`);
        }
      }
    }

    // Validação de desconto
    if (dadosInicializacao?.descontoMaximo) {
      const descontoValue = parseCurrency(formData.desconto);
      if (descontoValue > dadosInicializacao.descontoMaximo) {
        errors.push(`Desconto não pode ser maior que R$ ${dadosInicializacao.descontoMaximo.toFixed(2)}`);
      }
    }

    return errors;
  }, [formData, propertySelection.selectedUnidade, dadosInicializacao, valorFinal]);

  // Função auxiliar para construir payload de simulação
  const buildSimulationPayload = useCallback(() => {
    const planoSelecionado = planosDisponiveis[formData.plano];
    const tipoPlanoFlexChave = PLANO_TO_API_KEY[planoSelecionado];
    const tipoPlanoFlexValue = TIPO_PLANO_FLEX_TO_VALUE[tipoPlanoFlexChave] || 0;
    const tipoFluxoValue = Number.parseInt(formData.tipoFluxo);
    const tipoCorrecaoValue = TIPO_CORRECAO_TO_VALUE[formData.tipoCorrecao] || 0;

    const faixaComercialTexto = propertySelection.selectedUnidadeData?.tipoFaixaComercial || 'NA';
    const faixaComercial = FAIXA_TO_API[faixaComercialTexto] || 'NA';

    const intermediariasPayload = {};
    if (intermediates?.length > 0) {
      intermediates.forEach((intermediaria, index) => {
        const fieldName = `IntermediariaFlex${index + 1}`;
        const data = new Date(intermediaria.AnoIntermediaria, intermediaria.MesIntermediaria - 1, 1);
        intermediariasPayload[fieldName] = {
          Valor: intermediaria.Valor,
          data: data.toISOString()
        };
      });
    }

    return {
      IdUnidade: propertySelection.selectedUnidade,
      IdJornada: "00000000-0000-0000-0000-000000000000",
      Sinal1: formData.cartaDeCredito ? parseCurrency(formData.valorCartaDeCredito) : parseCurrency(formData.ato),
      Sinal2: parseCurrency(formData.e2),
      Sinal3: parseCurrency(formData.e3),
      CartaDeCredito: Boolean(formData.cartaDeCredito),
      QuantidadeParcelasMensais: Number.parseInt(formData.quantidadeParcelas) || 0,
      TipoPlanoFlex: tipoPlanoFlexValue,
      FaixaComercial: faixaComercial,
      Pagadoria: propertySelection.isPagadoria,
      Aumento: parseCurrency(formData.aumento),
      Desconto: parseCurrency(formData.desconto),
      Fgts: parseCurrency(formData.fgts),
      PadraoPsa: parseCurrency(formData.padraoPsa) || 0,
      ValorTabela: propertySelection.selectedUnidadeData?.unidadePreco?.valorImovelComDelta || 0,
      VINivel3: propertySelection.selectedUnidadeData?.unidadePreco?.valorImovelCaracteristicaComercialProduto || 0,
      TipoFluxo: tipoFluxoValue,
      TipoCorrecao: tipoCorrecaoValue,
      Renda: parseCurrency(formData.renda),
      Carteira: parseCurrency(formData.carteira),
      RendaExterior: propertySelection.isRendaExterior,
      Sad: formData.eSAD || false,
      Legado: formData.eSAD ? formData.legado || false : false,
      Padronizacao: propertySelection.selectedEmpreendimentoData?.padronizacao?.nome || '',
      ...intermediariasPayload,
      Amp: propertySelection.selectedUnidadeData?.bloco?.acompanhamentoMensalProducao ?? null,
      Bonus: propertySelection.selectedUnidadeData?.bonus ?? null,
      BomPagadorPagadoria: propertySelection.selectedUnidadeData?.bomPagadorPagadoria ?? null,
      VersaoTabelaPrecos: propertySelection.selectedUnidadeData?.unidadePreco?.versaoTabelaPrecos || null,
      IdCidade: propertySelection.selectedEmpreendimentoData?.cidade?.id || '00000000-0000-0000-0000-000000000000',
      IdEmpreendimento: propertySelection.selectedEmpreendimento || '00000000-0000-0000-0000-000000000000',
      ProSolutoA: propertySelection.selectedUnidadeData?.prosolutoA ?? null
    };
  }, [formData, propertySelection, planosDisponiveis, intermediates]);

  // Ocultar resultado quando houver alterações no formulário
  const hideResult = useCallback(() => {
    setShowResult(false);
    setIsPrintEnabled(false);
  }, []);

  // Enviar simulação para aprovação
  const enviarAprovacao = useCallback(async () => {
    const errors = validateForm();

    if (errors.length > 0) {
      showError(errors.join('; '));
      return;
    }

    const planoSelecionado = planosDisponiveis[formData.plano];
    const tipoPlanoFlexChave = PLANO_TO_API_KEY[planoSelecionado];
    
    if (!tipoPlanoFlexChave) {
      showError('Erro: Plano inválido selecionado');
      return;
    }

    const payload = buildSimulationPayload();

    try {
      setLoadingSimulacao(true);
      console.log('[enviarAprovacao] Iniciando simulação com payload:', payload);
      
      const resultado = await simularPropostaFlex(payload);
      
      console.log('[enviarAprovacao] Simulação concluída:', resultado);
      setSimulationResult(resultado);
      setShowResult(true);
      setIsPrintEnabled(true);
      showSuccess('Proposta enviada para aprovação com sucesso!');
      
    } catch (error) {
      console.error('[enviarAprovacao] Erro ao enviar proposta:', error);
      showError('Erro ao enviar proposta: ' + error.message);
      setShowResult(false);
      setSimulationResult(null);
    } finally {
      setLoadingSimulacao(false);
    }
  }, [
    validateForm,
    formData.plano,
    planosDisponiveis,
    buildSimulationPayload,
    showSuccess,
    showError
  ]);

  // Resetar estado de simulação
  const resetSimulationState = useCallback(() => {
    setDadosInicializacao(null);
    setValorFinal(0);
    setValorTabelaState(0);
    setSimulationResult(null);
    setShowResult(false);
    setIsPrintEnabled(false);
  }, []);

  // Função auxiliar para distribuir valores por plano
  const distribuirValoresPorPlano = useCallback((planoSelecionado, resultado, validarValor, formatToBrazilianDecimal) => {
    const isPlanoFixo = planoSelecionado === ALL_PLANOS.Fixo12 || planoSelecionado === ALL_PLANOS.Fixo18;
    const quantidadeParcelas = resultado.quantidadeMaximaParcelasMensais || 0;

    if (isPlanoFixo && resultado.valorFinal > 0 && quantidadeParcelas > 0) {
      const valorPorParcela = resultado.valorFinal / quantidadeParcelas;
      console.log('[inicializarProposta] Plano Fixo - Distribuindo valor final:', {
        valorFinal: resultado.valorFinal,
        quantidadeParcelas,
        valorPorParcela
      });
      
      return {
        quantidadeParcelas,
        ato: formatToBrazilianDecimal(valorPorParcela),
        e2: formatToBrazilianDecimal(valorPorParcela),
        e3: formatToBrazilianDecimal(valorPorParcela)
      };
    }

    const isPlanoAVista = planoSelecionado === ALL_PLANOS.AVista;
    const isPlanoPrazoObra = planoSelecionado === ALL_PLANOS.PrazoObra;
    
    if (isPlanoAVista && resultado.valorFinal > 0) {
      const atoValue = resultado.valoresPadrao?.ato || 0;
      const restante = resultado.valorFinal - atoValue;
      const valorE2E3 = restante > 0 ? restante / 2 : 0;
      
      console.log('[inicializarProposta] Plano À Vista - Redistribuindo:', {
        valorFinal: resultado.valorFinal,
        ato: atoValue,
        restante,
        e2e3: valorE2E3
      });
      
      return {
        quantidadeParcelas: 0,
        ato: formatToBrazilianDecimal(atoValue),
        e2: formatToBrazilianDecimal(valorE2E3),
        e3: formatToBrazilianDecimal(valorE2E3)
      };
    }
    
    if (isPlanoPrazoObra) {

      return {
        quantidadeParcelas: resultado.quantidadeParcelasPSA || 0,
        ato: validarValor(resultado.valoresPadrao?.ato, resultado.limites?.ato) || '',
        e2: validarValor(resultado.valoresPadrao?.e2, resultado.limites?.e2) || '',
        e3: validarValor(resultado.valoresPadrao?.e3, resultado.limites?.e3) || ''
      };
    }

    return {
      quantidadeParcelas: 0,
      ato: validarValor(resultado.valoresPadrao?.ato, resultado.limites?.ato) || '',
      e2: validarValor(resultado.valoresPadrao?.e2, resultado.limites?.e2) || '',
      e3: validarValor(resultado.valoresPadrao?.e3, resultado.limites?.e3) || ''
    };
  }, []);

  // Limpar campos quando há alteração em estado, cidade, empreendimento, bloco, unidade, pagadoria ou renda exterior
  useEffect(() => {
    if (!formData.plano) return;

    setFormData(prev => ({
      ...prev,
      ato: '',
      e2: '',
      e3: '',
      quantidadeParcelas: 0,
      fgts: '0',
      desconto: '0',
      aumento: '0',
      renda: '0',
      carteira: '0',
      padraoPsa: '0'
    }));
    
    setDadosInicializacao(null);
    setValorFinal(0);
    setValorTabelaState(0);
  }, [
    propertySelection.selectedEstado,
    propertySelection.selectedCidade,
    propertySelection.selectedEmpreendimento,
    propertySelection.selectedBloco,
    propertySelection.selectedUnidade,
    propertySelection.isPagadoria,
    propertySelection.isRendaExterior
  ]);

  // Chamar API de inicialização quando o plano é selecionado
  useEffect(() => {
    const inicializarQuandoPlanoSelecionado = async () => {
      // Verificar se temos todos os dados necessários
      if (!formData.plano || 
          !propertySelection.selectedUnidade || 
          !propertySelection.selectedEmpreendimentoData ||
          !propertySelection.selectedUnidadeData) {
        return;
      }

      const planoSelecionado = planosDisponiveis[formData.plano];
      if (!planoSelecionado) {
        console.warn('[inicializarProposta] Plano não encontrado no array planosDisponiveis');
        return;
      }

      // Converter plano para chave da API
      const tipoPlanoFlexChave = PLANO_TO_API_KEY[planoSelecionado];
      if (!tipoPlanoFlexChave) {
        console.warn('[inicializarProposta] Chave da API não encontrada para o plano:', planoSelecionado);
        return;
      }

      // Converter chave para valor numérico (TipoPlanoFlex)
      const tipoPlanoFlexValue = TIPO_PLANO_FLEX_TO_VALUE[tipoPlanoFlexChave] || 0;
      if (tipoPlanoFlexValue === 0) {
        console.warn('[inicializarProposta] Valor numérico não encontrado para:', tipoPlanoFlexChave);
        return;
      }

      // Obter FaixaComercial da unidade (tipoFaixaComercial)
      const faixaComercialTexto = propertySelection.selectedUnidadeData?.tipoFaixaComercial || 'NA';
      const faixaComercial = FAIXA_TO_API[faixaComercialTexto] || 'NA';

      // Obter ValorTabela da unidade
      const valorTabela = propertySelection.selectedUnidadeData?.unidadePreco?.valorImovelComDelta || 0;

      // Obter IdCidade do empreendimento
      const idCidade = propertySelection.selectedEmpreendimentoData?.cidade?.id || null;

      // Obter IdEmpreendimento
      const idEmpreendimento = propertySelection.selectedEmpreendimento || null;

      // Obter Amp do bloco da unidade
      const amp = propertySelection.selectedUnidadeData?.bloco?.acompanhamentoMensalProducao ?? null;

      // Obter Bonus e BomPagadorPagadoria da unidade
      const bonus = propertySelection.selectedUnidadeData?.bonus ?? null;
      const bomPagadorPagadoria = propertySelection.selectedUnidadeData?.bomPagadorPagadoria ?? null;

      // Montar payload
      const payload = {
        IdUnidade: propertySelection.selectedUnidade,
        IdCidade: idCidade,
        IdEmpreendimento: idEmpreendimento,
        TipoPlanoFlex: tipoPlanoFlexValue,
        FaixaComercial: faixaComercial,
        Pagadoria: propertySelection.isPagadoria,
        ValorTabela: valorTabela,
        Amp: amp,
        Bonus: bonus,
        BomPagadorPagadoria: bomPagadorPagadoria,
        ProSolutoA: propertySelection.selectedUnidadeData?.prosolutoA ?? null,
        Sad: formData.eSAD || false
      };

      try {
        setLoadingInicializacao(true);
        console.log('[inicializarProposta] Iniciando chamada com payload:', payload);
        const resultado = await inicializarProposta(payload);
        console.log('[inicializarProposta] Sucesso:', resultado);
        
        // Armazenar dados da inicialização
        setDadosInicializacao(resultado);
        setValorTabelaState(resultado.valorTabela || 0);
        setValorFinal(resultado.valorFinal || 0);
        
        // Validar e ajustar valores padrão com base nos limites
        const validarValor = (valor, limite) => {
          if (!valor || !limite) return valor;
          const numValor = Number.parseFloat(valor);
          if (Number.isNaN(numValor)) return valor;
          
          if (numValor < limite.minimo) return limite.minimo;
          if (numValor > limite.maximo) return limite.maximo;
          return numValor;
        };

        // Formatar valor para padrão brasileiro (vírgula como decimal)
        const formatToBrazilianDecimal = (value) => {
          return value.toFixed(2).replace('.', ',');
        };

        // Distribuir valores conforme o plano
        const valoresDistribuidos = distribuirValoresPorPlano(planoSelecionado, resultado, validarValor, formatToBrazilianDecimal);
        
        // Limpar campos que precisam ser resetados quando inicializa
        const camposParaLimpar = {
          fgts: '0',
          desconto: '0',
          aumento: '0',
          renda: '0',
          carteira: '0',
          padraoPsa: '0'
        };
        
        setFormData(prev => ({ ...prev, ...camposParaLimpar, ...valoresDistribuidos }));
        
        showSuccess('Proposta inicializada com sucesso!');
      } catch (error) {
        console.error('[inicializarProposta] Erro ao inicializar:', error);
        showError('Erro ao inicializar proposta: ' + error.message);
      } finally {
        setLoadingInicializacao(false);
      }
    };

    inicializarQuandoPlanoSelecionado();
  }, [
    formData.plano,
    formData.eSAD,
    planosDisponiveis, 
    propertySelection.selectedUnidade,
    propertySelection.selectedEmpreendimentoData,
    propertySelection.selectedUnidadeData,
    propertySelection.isPagadoria,
    propertySelection.isRendaExterior
  ]);

  return {
    dadosInicializacao,
    valorFinal,
    valorTabelaState,
    loadingInicializacao,
    loadingSimulacao,
    simulationResult,
    showResult,
    isPrintEnabled,
    enviarAprovacao,
    resetSimulationState,
    hideResult
  };
};
